import api from '../utils/api';

const BASE = '/api/logs';

/**
 * List a page of logs across all scenarios the authenticated user created,
 * optionally filtered by a search term matched server-side against viewer
 * usernames and scenario titles.
 * @route GET /api/logs
 * @param {Object} [options]
 * @param {number} [options.page=1]
 * @param {number} [options.limit=10]
 * @param {string} [options.search] - matches viewer username or scenario title
 * @param {string} [options.scenarioId] - restrict to a single owned scenario
 * @returns {Promise<{ logs: Object[], total: number, page: number, totalPages: number }>}
 */
export const getLogs = ({ page = 1, limit = 10, search, scenarioId } = {}) =>
    api.get(BASE, {
        params: {
            page,
            limit,
            search: search || undefined,
            scenarioId: scenarioId || undefined,
        },
    }).then((r) => r.data);

/**
 * Get a single log with fully populated, formatted decisions.
 * @route GET /api/logs/details/:logId
 * @param {string} logId
 * @returns {Promise<Object>} The log plus a formattedDecisions array
 */
export const getLogDetails = (logId) =>
    api.get(`${BASE}/details/${logId}`).then((r) => r.data);

/**
 * Delete a log.
 * @route DELETE /api/logs/:logId
 * @param {string} logId
 * @returns {Promise<Object>} The deleted log document
 */
export const deleteLog = (logId) =>
    api.delete(`${BASE}/${logId}`).then((r) => r.data);

/**
 * Get the caller's in-progress log for a scenario, or create one.
 * Auth is optional — logged-in callers are tracked by viewer, anonymous callers
 * by the x-anonymous-id header.
 * @route POST /api/logs/getOrCreateLog
 * @param {string} scenarioId
 * @param {string} startSituationId - required only when a new log must be created
 * @param {string} [assignmentId]
 * @param {string} [anonId] - anonymous viewer id, sent as the x-anonymous-id header
 * @returns {Promise<Object>} The existing or newly created log
 */
export const getOrCreateLog = (scenarioId, startSituationId, assignmentId, anonId) =>
    api.post(`${BASE}/getOrCreateLog`,
        { scenarioId, startSituationId, assignmentId },
        { withCredentials: true, headers: { 'x-anonymous-id': anonId } }
    ).then((r) => r.data);

/**
 * Get the caller's in-progress log for a scenario, or null. Read-only (never creates).
 * Used on scenario entry to resume a partially-completed run.
 * @route GET /api/logs/active?scenarioId=...
 * @param {string} scenarioId
 * @param {string} [anonId] - anonymous viewer id, sent as the x-anonymous-id header
 * @returns {Promise<{ log: Object|null }>}
 */
export const getActiveLog = (scenarioId, anonId) =>
    api.get(`${BASE}/active`, {
        params: { scenarioId },
        withCredentials: true,
        headers: { 'x-anonymous-id': anonId }
    }).then((r) => r.data);

/**
 * Append a decision to a log and/or advance its state (current situation, completion).
 * @route PATCH /api/logs/:logId/submitDecision
 * @param {string} logId
 * @param {Object} payload - { situationId, choiceId, userInput, nextSituationId, correctness, isCompleted, fromToC, assignmentId, type }
 * @returns {Promise<{ success: boolean, priority?: boolean }>}
 */
export const submitDecision = (logId, payload) =>
    api.patch(`${BASE}/${logId}/submitDecision`, payload, { withCredentials: true }).then((r) => r.data);
